import {
  Box,
  Button,
  Stack,
  Card,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Spinner from 'src/utils/Loader/spinner';
import { useRightSidebar } from 'src/layouts/right-side-bar/rightSidebarProvider';
import HttpServices from 'src/services/httpService';
import { reportModuleURL } from 'src/services/urlService';
import { useToaster } from 'src/utils/toaster/toasterContext';
import AddCard from './add-card.component';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { useNavigate } from 'react-router-dom';
import CustomReportHeaderComponent from 'src/components/reports/custom.report.header.component';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const CustomReport = () => {
  const { t: translate } = useTranslation();
  const { showRightSidebar } = useRightSidebar();
  const navigate = useNavigate();
  const { showToast } = useToaster();

  // State Variables
  const [isLoading, setLoading] = useState(false);
  const [customReports, setCustomReports] = useState([]);
  const [currentCustomReport, setCurrentCustomReport] = useState(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newReportName, setNewReportName] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [confirmationDialogue, setConfirmationDialogue] = useState(null);

  // Fetch report details on mount
  useEffect(() => {
    fetchCustomReports();
  }, []);

  const fetchCustomReports = () => {
    setLoading(true);
    const currentReportId = currentCustomReport?.custom_report_id; // Preserve current report ID
    let getProps = {
      url: reportModuleURL.fetchCustomReports,
      successCallback: (data, message) => successCallback(data, message, currentReportId),
      failureCallback,
    };
    HttpServices.Get(getProps);
  };

  const successCallback = (data, message, preservedReportId) => {
    showToast(message, 's');
    setLoading(false);

    const filteredData = data.map((report) => ({
      ...report,
      report_cards: report.report_cards?.filter((card) => card.is_active === true && card.is_enabled === true) || [],
    }));

    setCustomReports(filteredData);

    if (filteredData?.length > 0) {
      const matchingReport = filteredData.find((report) => report.custom_report_id === preservedReportId);
      setCurrentCustomReport(matchingReport || filteredData[0]);
    } else {
      setCurrentCustomReport(null);
    }
  };
  const failureCallback = (message) => {
    showToast(message, 'e');
    setLoading(false);
  };

  const handleCustomReport = (event) => {
    const value = event.target.value;
    if (value === 'CREATE-CUSTOM-REPORT') {
      setOpenCreateDialog(true);
      setIsEditMode(false);
      setNewReportName('');
    } else {
      const selectedReport = customReports.find((report) => report.custom_report_id === value);
      setCurrentCustomReport(selectedReport);
    }
  };

  const handleOpenScheduled = () => {
    if (!currentCustomReport) {
      showToast(translate('custom_report.select_report_to_add_card'), 'e');
      return;
    }
    let data = {
      content: sideBarScheduledView,
    };
    showRightSidebar(data);
  };

  const sideBarScheduledView = () => {
    return (
      <AddCard customReportId={currentCustomReport?.custom_report_id} fetchCustomReportDetails={fetchCustomReports} />
    );
  };

  const handleCreateReport = () => {
    if (!newReportName.trim()) {
      showToast(translate('custom_report.report_name_required'), 'e');
      return;
    }
    setConfirmationDialogue({
      isOpen: true,
      title: translate('custom_report.confirm_create_report'),
      message: translate('custom_report.confirm_create_report_message', { name: newReportName }),
      submitFunction: () => {
        const postProps = {
          url: reportModuleURL.createCustomReport,
          body: { name: newReportName },
          successCallback: (data, message) => {
            showToast(message, 's');
            setOpenCreateDialog(false);
            setNewReportName('');
            fetchCustomReports();
            setConfirmationDialogue(null);
          },
          failureCallback: (message) => {
            showToast(message, 'e');
            setConfirmationDialogue(null);
          },
        };
        HttpServices.Post(postProps);
      },
    });
  };

  const handleEditReport = () => {
    if (!newReportName.trim()) {
      showToast(translate('custom_report.report_name_required'), 'e');
      return;
    }
    setConfirmationDialogue({
      isOpen: true,
      title: translate('custom_report.confirm_edit_report'),
      message: translate('custom_report.confirm_edit_report_message', { name: newReportName }),
      submitFunction: () => {
        const postProps = {
          url: reportModuleURL.updateCustomReport,
          body: {
            custom_report_id: editingReport?.custom_report_id,
            name: newReportName,
            start_date: editingReport?.start_date,
            end_date: editingReport?.end_date,
          },
          successCallback: (data, message) => {
            showToast(message, 's');
            handleCloseDialog();
            fetchCustomReports();
            setConfirmationDialogue(null);
          },
          failureCallback: (message) => {
            showToast(message, 'e');
            setConfirmationDialogue(null);
          },
        };
        HttpServices.Post(postProps);
      },
    });
  };

  const handleDeleteReport = () => {
    if (!currentCustomReport) {
      showToast(translate('custom_report.select_report_to_delete'), 'e');
      return;
    }
    setConfirmationDialogue({
      isOpen: true,
      title: translate('custom_report.confirm_delete_report'),
      message: translate('custom_report.confirm_delete_report_message', {
        name: currentCustomReport?.custom_report_name,
      }),
      submitFunction: () => {
        const postProps = {
          url: reportModuleURL.deleteCustomReport,
          body: { custom_report_id: currentCustomReport?.custom_report_id },
          successCallback: (data, message) => {
            showToast(message, 's');
            setCurrentCustomReport(null);
            fetchCustomReports();
            setConfirmationDialogue(null);
          },
          failureCallback: (message) => {
            showToast(message, 'e');
            setConfirmationDialogue(null);
          },
        };
        HttpServices.Post(postProps);
      },
    });
  };

  const handleSaveReport = () => {
    if (!currentCustomReport) {
      showToast(translate('custom_report.select_report_to_save'), 'e');
      return;
    }
    setConfirmationDialogue({
      isOpen: true,
      title: translate('custom_report.confirm_save'),
      message: translate('custom_report.confirm_save_report_message', {
        name: currentCustomReport?.custom_report_name,
      }),
      submitFunction: () => {
        const postProps = {
          url: reportModuleURL.updateCustomReport,
          body: {
            custom_report_id: currentCustomReport?.custom_report_id,
            name: currentCustomReport?.custom_report_name,
            start_date: currentCustomReport?.start_date,
            end_date: currentCustomReport?.end_date,
          },
          successCallback: (data, message) => {
            showToast(message, 's');
            fetchCustomReports();
            setConfirmationDialogue(null);
          },
          failureCallback: (message) => {
            showToast(message, 'e');
            setConfirmationDialogue(null);
          },
        };
        HttpServices.Post(postProps);
      },
    });
  };

  const handleCloseDialog = () => {
    setOpenCreateDialog(false);
    setNewReportName('');
    setIsEditMode(false);
    setEditingReport(null);
    setConfirmationDialogue(null);
  };

  return (
    <>
      <Helmet>
        <title>{translate('navbar.menu.social_media_report')}</title>
      </Helmet>
      {isLoading ? (
        <Card sx={{ minHeight: 'calc(100vh - 64px)', maxHeight: 'calc(100vh - 64px)' }}>
          <Spinner />
        </Card>
      ) : currentCustomReport ? (
        <>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2, flexWrap: 'wrap' }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Select
                size="small"
                value={currentCustomReport?.custom_report_id || 'ALL'}
                onChange={handleCustomReport}
                displayEmpty
                renderValue={(selected) => {
                  if (selected === 'ALL') {
                    return translate('custom_report.select_all');
                  }
                  if (!selected) {
                    return translate('custom_report.select_report');
                  }
                  const selectedReport = customReports.find((report) => report.custom_report_id === selected);
                  return selectedReport?.custom_report_name || translate('custom_report.select_report');
                }}
                sx={{
                  minWidth: 200,
                  maxWidth: 300, // ← add this line to set a maximum width
                  height: 36,
                  bgcolor: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                      maxWidth: 300, // ← optional: also restrict menu dropdown width
                    },
                  },
                }}
              >
                {customReports?.map((report) => (
                  <MenuItem key={report.custom_report_id} value={report.custom_report_id}>
                    {report.custom_report_name}
                  </MenuItem>
                ))}
                <MenuItem value="CREATE-CUSTOM-REPORT">
                  <Box sx={{ display: 'flex', fontSize: '15px', alignItems: 'center', gap: 1 }}>
                    <AddCircleIcon fontSize="large" sx={{ color: 'primary.main' }} />
                    {translate('custom_report.create_custom_report')}
                  </Box>
                </MenuItem>
              </Select>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 1,
                  overflow: 'hidden',
                  height: 36,
                }}
              >
                <Tooltip title={translate('custom_report.edit_report')}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingReport(currentCustomReport);
                      setNewReportName(currentCustomReport.custom_report_name);
                      setOpenCreateDialog(true);
                      setIsEditMode(true);
                    }}
                    sx={{
                      borderRadius: 0,
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.light',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={translate('custom_report.delete_report')}>
                  <IconButton
                    size="small"
                    onClick={handleDeleteReport}
                    sx={{
                      borderRadius: 0,
                      color: 'error.main',
                      '&:hover': {
                        bgcolor: 'error.light',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Tooltip title={translate('custom_report.add_card')}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleOpenScheduled}
                  sx={{
                    minWidth: 80,
                    height: 36,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                      bgcolor: 'primary.dark',
                    },
                  }}
                >
                  {translate('custom_report.add_card')}
                </Button>
              </Tooltip>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button variant="contained" size="small" onClick={() => navigate('/scheduled-report-view')}>
                <Tooltip title={translate('custom_report.view_scheduled_reports')}>
                  <span>{translate('custom_report.view_scheduled_reports')}</span>
                </Tooltip>
              </Button>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={translate('upcoming_post.start_date')}
                  value={dayjs(currentCustomReport.start_date).toDate()}
                  onChange={(newValue) =>
                    setCurrentCustomReport({
                      ...currentCustomReport,
                      start_date: dayjs(newValue).toISOString(),
                    })
                  }
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 140, height: 36 },
                    },
                  }}
                />
                <DatePicker
                  label={translate('upcoming_post.end_date')}
                  value={dayjs(currentCustomReport.end_date).toDate()}
                  onChange={(newValue) =>
                    setCurrentCustomReport({
                      ...currentCustomReport,
                      end_date: dayjs(newValue).toISOString(),
                    })
                  }
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 140, height: 36 },
                    },
                  }}
                />
              </LocalizationProvider>
              <Tooltip title={translate('custom_report.save_report')}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={handleSaveReport}
                  sx={{
                    minWidth: 80,
                    height: 36,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                      bgcolor: 'success.dark',
                    },
                  }}
                >
                  {translate('custom_report.save')}
                </Button>
              </Tooltip>
            </Stack>
          </Stack>
          <CustomReportHeaderComponent customReportData={currentCustomReport} fetchCustomReports={fetchCustomReports} />
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 'calc(100vh - 64px)', // Adjust for header height
            bgcolor: 'background.default',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<AddCircleIcon />}
            onClick={() => {
              setOpenCreateDialog(true);
              setIsEditMode(false);
              setNewReportName('');
            }}
            sx={{
              padding: '12px 24px',
              fontSize: '1.1rem',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: '0 6px 12px rgba(0,0,0,0.3)',
                bgcolor: 'primary.dark',
              },
            }}
          >
            {translate('custom_report.create_custom_report')}
          </Button>
        </Box>
      )}
      <Dialog
        open={openCreateDialog}
        onClose={handleCloseDialog}
        maxWidth="sm" // Makes dialog bigger
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0px 6px 20px rgba(0,0,0,0.15)',
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: '1.3rem',
            pb: 1,
            color: 'primary.main',
          }}
        >
          {isEditMode ? translate('custom_report.edit_report') : translate('custom_report.create_report')}
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label={translate('custom_report.report_name')}
            variant="outlined"
            value={newReportName}
            onChange={(e) => setNewReportName(e.target.value)}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            color="error"
          >
            {translate('cancel')}
          </Button>
          <Button
            onClick={isEditMode ? handleEditReport : handleCreateReport}
            variant="contained"
          >
            {translate('custom_report.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {confirmationDialogue && (
        <ConfirmationDialog
          open={confirmationDialogue.isOpen}
          onClose={() => setConfirmationDialogue(null)}
          onConfirm={confirmationDialogue.submitFunction}
          title={confirmationDialogue.title}
          message={confirmationDialogue.message}
        />
      )}
    </>
  );
};

export default CustomReport;
